'use client'

import React, { useEffect, useRef, useState } from 'react'
import type { Editor, Component } from 'grapesjs'
import { initializeStyleManager } from '@/app/(main)/subaccount/[subaccountId]/landings/[landingId]/builder/[landingPageId]/_components/StyleManager'

interface GrapesJSStylePanelProps {
  editor: Editor | null
  onAutoFocus?: () => void
}

const GrapesJSStylePanel: React.FC<GrapesJSStylePanelProps> = ({
  editor,
  onAutoFocus
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedComponent, setSelectedComponent] = useState<Component | null>(
    null
  )

  useEffect(() => {
    if (!editor || !containerRef.current) return

    // Function to handle component selection changes
    const handleComponentSelection = (component: Component | null) => {
      setSelectedComponent(component)
      updateStylePanel(component)
    }

    const updateStylePanel = (component: Component | null) => {
      if (!containerRef.current) return

      containerRef.current.innerHTML = ''

      try {
        // Initialize the style manager with necessary sectors and properties
        initializeStyleManager(editor)

        // Create Classes Panel (Selector Manager)
        const selectorManager = editor.SelectorManager
        const selectorContainer = document.createElement('div')
        selectorContainer.className = 'gjs-sm-sector gjs-sm-classes'
        selectorContainer.innerHTML = `
          <div class="gjs-sm-title">Classes</div>
          <div class="gjs-classes-container"></div>
        `

        // Get the style manager
        const styleManager = editor.StyleManager

        // Render the style manager directly into our container
        const styleManagerEl = styleManager.render()

        // This ensures classes appear at the top of the style panel
        styleManagerEl.insertBefore(
          selectorContainer,
          styleManagerEl.firstChild
        )

        // Create the selector manager UI inside the classes container
        const classesContainer = selectorContainer.querySelector(
          '.gjs-classes-container'
        )
        if (classesContainer) {
          const selectorEl = selectorManager.render([])
          classesContainer.appendChild(selectorEl)
        }

        if (!component) {
          const noSelectionMessage = document.createElement('div')
          noSelectionMessage.className = 'no-selection-message'
          noSelectionMessage.innerHTML = `
            <div class="flex items-center justify-center h-32 text-gray-400 p-4">
              <div class="text-center">
                <div class="text-sm">No element selected</div>
                <div class="text-xs mt-1 opacity-60">Click on an element to edit its styles</div>
              </div>
            </div>
          `
          styleManagerEl.insertBefore(
            noSelectionMessage,
            styleManagerEl.firstChild
          )
        }

        containerRef.current.appendChild(styleManagerEl)

        // Add custom styling for selector manager
        addSelectorStyles()
      } catch (error) {
        console.error('Error rendering style panel:', error)
        if (containerRef.current) {
          containerRef.current.innerHTML = `<div class="flex items-center justify-center h-full text-red-400">
              Error loading style panel: ${error}
            </div>`
        }
      }
    }

    const onComponentSelected = (component: Component) => {
      handleComponentSelection(component)
    }

    const onComponentDeselected = () => {
      handleComponentSelection(null)
    }

    const onComponentStyleUpdate = (component: Component) => {
      if (component === selectedComponent) {
        updateStylePanel(component)
      }
    }

    const onCustomSelectionEvent = (event: Event) => {
      const customEvent = event as CustomEvent
      const component = customEvent.detail?.component
      if (component) {
        handleComponentSelection(component)
      }
    }

    const onCustomDeselectionEvent = () => {
      handleComponentSelection(null)
    }

    editor.on('component:selected', onComponentSelected)
    editor.on('component:deselected', onComponentDeselected)
    editor.on('component:styleUpdate', onComponentStyleUpdate)

    window.addEventListener('gjs:component:selected', onCustomSelectionEvent)
    window.addEventListener(
      'gjs:component:deselected',
      onCustomDeselectionEvent
    )

    const currentSelected = editor.getSelected()
    if (currentSelected) {
      handleComponentSelection(currentSelected)
    } else {
      updateStylePanel(null)
    }

    // Clear the container
    containerRef.current.innerHTML = ''

    try {
      // Initialize the style manager with necessary sectors and properties
      initializeStyleManager(editor)

      // Create Classes Panel (Selector Manager)
      const selectorManager = editor.SelectorManager
      const selectorContainer = document.createElement('div')
      selectorContainer.className = 'gjs-sm-sector gjs-sm-classes'
      selectorContainer.innerHTML = `
        <div class="gjs-sm-title">Classes</div>
        <div class="gjs-classes-container"></div>
      `

      // Get the style manager
      const styleManager = editor.StyleManager

      // Render the style manager directly into our container
      const styleManagerEl = styleManager.render()

      // This ensures classes appear at the top of the style panel
      styleManagerEl.insertBefore(selectorContainer, styleManagerEl.firstChild)

      // Create the selector manager UI inside the classes container
      const classesContainer = selectorContainer.querySelector(
        '.gjs-classes-container'
      )
      if (classesContainer) {
        const selectorEl = selectorManager.render([])
        classesContainer.appendChild(selectorEl)
      }

      // Append the style manager element to our container
      containerRef.current.appendChild(styleManagerEl)

      // Add custom styling for selector manager
      addSelectorStyles()

      // Set up component selection handler for auto-expansion
      const handleComponentSelection = (component: Component) => {
        if (!component) return

        if (onAutoFocus) {
          onAutoFocus()
        }

        expandRelevantStyleSections(component)
      }

      editor.on('component:selected', handleComponentSelection)
      return () => {
        editor.off('component:selected', handleComponentSelection)
      }
    } catch (error) {
      console.error('Error rendering style panel:', error)
      if (containerRef.current) {
        containerRef.current.innerHTML = `<div class="flex items-center justify-center h-full text-red-400">
            Error loading style panel: ${error}
          </div>`
      }
    }

    // Clean up on unmount
    return () => {
      editor.off('component:selected', onComponentSelected)
      editor.off('component:deselected', onComponentDeselected)
      editor.off('component:styleUpdate', onComponentStyleUpdate)

      window.removeEventListener(
        'gjs:component:selected',
        onCustomSelectionEvent
      )
      window.removeEventListener(
        'gjs:component:deselected',
        onCustomDeselectionEvent
      )

      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }

      // Remove custom styling
      const styleEl = document.getElementById('gjs-selector-custom-style')
      if (styleEl) styleEl.remove()
    }
  }, [editor, onAutoFocus])

  const expandRelevantStyleSections = (component: Component) => {
    if (!containerRef.current) return

    const componentType = component.get('type')
    const componentName = component.getName()

    const sectors = containerRef.current.querySelectorAll('.gjs-sm-sector')
    const sectorsToExpand = getSectorsToExpand(componentType ?? '')

    sectors.forEach((sector: Element) => {
      const sectorTitle =
        sector.querySelector('.gjs-sm-title')?.textContent?.toLowerCase() || ''
      const sectorElement = sector as HTMLElement

      if (sectorsToExpand.some(s => sectorTitle.includes(s.toLowerCase()))) {
        sectorElement.classList.add('gjs-open')
        const content = sector.querySelector('.gjs-sm-properties')
        if (content) {
          ;(content as HTMLElement).style.display = 'block'
        }
      }
    })

    const firstExpandedSection = containerRef.current.querySelector(
      '.gjs-sm-sector.gjs-open'
    )
    if (firstExpandedSection) {
      firstExpandedSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const getSectorsToExpand = (componentType: string): string[] => {
    const sectorsToExpand: string[] = []
    sectorsToExpand.push('General', 'Dimension', 'Typography')

    switch (componentType) {
      case 'text':
      case 'textnode':
        sectorsToExpand.push('Typography', 'Decorations')
        break
      case 'image':
        sectorsToExpand.push('Dimension', 'Extra')
        break
      case 'link':
        sectorsToExpand.push('Typography', 'Decorations')
        break
      case 'button':
        sectorsToExpand.push('Typography', 'Decorations', 'Extra')
        break
      case 'input':
      case 'textarea':
      case 'select':
        sectorsToExpand.push('Typography', 'Extra')
        break
      case 'video':
        sectorsToExpand.push('Dimension', 'Extra')
        break
      case 'map':
        sectorsToExpand.push('Dimension', 'Extra')
        break
      default:
        sectorsToExpand.push('Flex', 'Dimension')
        break
    }

    return sectorsToExpand
  }

  const addSelectorStyles = () => {
    // Remove existing style if there's one
    const existingStyle = document.getElementById('gjs-selector-custom-style')
    if (existingStyle) existingStyle.remove()

    // Create style element
    const style = document.createElement('style')
    style.id = 'gjs-selector-custom-style'
    style.innerHTML = `
      .gjs-sm-classes {
        border-bottom: 1px solid rgba(0,0,0,0.2);
        padding-bottom: 10px;
        margin-bottom: 10px;
      }
      .gjs-sm-classes .gjs-sm-title {
        padding: 10px;
        font-weight: bold;
      }
      .gjs-classes-container {
        padding: 0 10px;
      }
      .no-selection-message {
        border-bottom: 1px solid rgba(0,0,0,0.2);
        margin-bottom: 10px;
      }
      
     
      .gjs-sm-sector,
      .gjs-sm-sectors,
      .gjs-sm-classes,
      .gjs-clm-tags,
      .gjs-sm-classes .gjs-sm-title,
      .gjs-classes-container {
        background-color: #2d3238 !important;
      }
      
         
      .gjs-clm-select, 
      .gjs-sm-select option {
        background-color: #373d49 !important;
        color: #ddd !important;
        border: 1px solid rgba(255,255,255,0.15) !important;
        border-radius: 3px !important;
      }
      
      /* Enhanced styles for better section visibility */
      .gjs-sm-sector.gjs-open .gjs-sm-title {
        background-color: rgba(232, 78, 54, 0.1) !important;
        border-left: 3px solid #E84E36 !important;
      }
      
      .gjs-sm-sector .gjs-sm-title {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .gjs-sm-sector .gjs-sm-title:hover {
        background-color: rgba(255, 255, 255, 0.05) !important;
      }
    `

    document.head.appendChild(style)
  }

  return (
    <div
      ref={containerRef}
      className='h-full overflow-auto bg-[#2d3238] p-2 text-white'
    >
      {!editor && (
        <div className='flex h-full items-center justify-center text-gray-400'>
          Editor not initialized
        </div>
      )}
    </div>
  )
}

export default GrapesJSStylePanel
